import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useCompetitions, useCreateCompetition } from '@/hooks/useCompetitions';
import { useOrganizations } from '@/hooks/useOrganizations';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState, Alert } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Table, type Column } from '@/components/ui/Table';
import type { Competition } from '@/api/types';
import { competitionStatusLabel, competitionStatusTone, FZS_PATRONAGE_LABEL } from '@/lib/competitions';
import { formatDateTime } from '@/lib/dates';

interface CreateFormState {
  organizationId: string;
  name: string;
  description: string;
}

const emptyCreateForm: CreateFormState = { organizationId: '', name: '', description: '' };

/**
 * The organizer's list of competitions: one row per competition, opening its own page. Everything about ONE
 * competition (state, settings, participants, awards, payments) lives on that page, not here.
 */
export function OrganizerCompetitionsPage() {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const competitions = useCompetitions({ page, pageSize });
  const organizations = useOrganizations({ page: 1, pageSize: 100 });
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const createCompetition = useCreateCompetition();
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState<CreateFormState>(emptyCreateForm);

  // The sidebar's "Novo tekmovanje" lands here with ?new=1 and goes straight into the form.
  const wantsNew = params.get('new') === '1';
  const orgsReady = organizations.isSuccess;
  useEffect(() => {
    if (wantsNew && orgsReady) {
      setCreateForm({ ...emptyCreateForm, organizationId: organizations.data?.items[0]?.id ?? '' });
      setIsCreating(true);
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsNew, orgsReady]);

  function openCreate() {
    setCreateForm({
      ...emptyCreateForm,
      organizationId: organizations.data?.items[0]?.id ?? '',
    });
    setIsCreating(true);
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    const created = await createCompetition.mutateAsync({
      organizationId: createForm.organizationId,
      name: createForm.name,
      description: createForm.description || undefined,
    });
    setIsCreating(false);
    navigate(`/organizer/competitions/${created.slug}/setup`);
  }

  const columns: Column<Competition>[] = [
    {
      header: 'Naziv',
      accessor: (c) => (
        <Link to={`/competitions/${c.slug}`} className="font-medium text-fg hover:underline">
          {c.name}
        </Link>
      ),
    },
    {
      header: 'Status',
      accessor: (c) => (
        <div className="flex flex-wrap gap-1.5">
          <Badge tone={competitionStatusTone(c.status)}>{competitionStatusLabel(c.status)}</Badge>
          {c.isFzsPatronage && <Badge tone="Fzs">{FZS_PATRONAGE_LABEL}</Badge>}
        </div>
      ),
    },
    {
      header: 'Rok za oddajo',
      accessor: (c) => (c.submissionDeadline ? formatDateTime(c.submissionDeadline) : '—'),
    },
    {
      header: '',
      accessor: (c) => (
        <Link to={`/competitions/${c.slug}`} className="inline-flex">
          <Button variant="secondary" size="sm">
            Odpri
            <ArrowRight size={14} aria-hidden="true" />
          </Button>
        </Link>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-fg">Vsa tekmovanja</h1>
        <Button onClick={openCreate} disabled={organizations.isLoading || !organizations.data?.items.length}>
          Novo tekmovanje
        </Button>
      </div>

      {organizations.isSuccess && organizations.data.items.length === 0 && (
        <Alert variant="error">
          Še ne obstaja nobena organizacija. Za ustvarjanje tekmovanja je potrebna organizacija.
        </Alert>
      )}

      {competitions.isLoading && <PageSpinner />}
      {competitions.isError && (
        <ErrorState message={getApiErrorMessage(competitions.error)} onRetry={() => competitions.refetch()} />
      )}
      {competitions.isSuccess && competitions.data.items.length === 0 && (
        <EmptyState title="Ni še tekmovanj" description="Ustvarite svoje prvo tekmovanje za začetek." />
      )}
      {competitions.isSuccess && competitions.data.items.length > 0 && (
        <Table columns={columns} rows={competitions.data.items} getRowKey={(c) => c.id} />
      )}

      {competitions.isSuccess && competitions.data.total > pageSize && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prejšnja
          </Button>
          <span className="text-sm text-fg-muted">Stran {page}</span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page * pageSize >= competitions.data.total}
            onClick={() => setPage((p) => p + 1)}
          >
            Naslednja
          </Button>
        </div>
      )}

      {isCreating && (
        <Modal title="Novo tekmovanje" onClose={() => setIsCreating(false)}>
          <form onSubmit={handleCreate} className="flex flex-col gap-3">
            {createCompetition.isError && <Alert variant="error">{getApiErrorMessage(createCompetition.error)}</Alert>}
            <Select
              label="Organizacija"
              value={createForm.organizationId}
              onChange={(e) => setCreateForm((f) => ({ ...f, organizationId: e.target.value }))}
              required
            >
              <option value="" disabled>
                Izberite organizacijo&hellip;
              </option>
              {organizations.data?.items.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </Select>
            <Input
              label="Naziv"
              value={createForm.name}
              onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
            <Input
              label="Opis"
              value={createForm.description}
              onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
            />
            <p className="text-xs text-fg-muted">
              Po ustvarjanju vas vodimo skozi nastavitve tekmovanja po korakih. Tekmovanje se začne v stanju Osnutek.
            </p>
            <Button
              type="submit"
              isLoading={createCompetition.isPending}
              disabled={!createForm.organizationId || !createForm.name}
              className="self-start"
            >
              Ustvari tekmovanje
            </Button>
          </form>
        </Modal>
      )}
    </div>
  );
}
